import React, { useEffect, useMemo, useRef, useState } from "react";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import useFormattingUtils from "../../../../context/useFormattingUtils";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import {
  FiFileText,
  FiUsers,
  FiCheckCircle,
  FiX,
  FiTruck,
  FiPackage,
  FiDollarSign,
  FiClock,
  FiShoppingCart,
  FiTrendingUp,
  FiPieChart,
  FiBarChart2,
  FiArchive,
  FiAlertCircle,
  FiTarget,
  FiActivity,
} from "react-icons/fi";
import "./Dashboard.css";
import "./ProductRfqDashborad/ProductRfqSummary.css";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const PIE_COLORS = ["#2563eb", "#059669", "#d97706", "#7c3aed", "#dc2626", "#0891b2", "#db2777"];

const safeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCount = (value) => safeNumber(value).toLocaleString("en-IN");

const getPercentage = (part, total) => {
  const p = safeNumber(part);
  const t = safeNumber(total);
  if (!t) return 0;
  return Math.min(100, Math.max(0, Math.round((p / t) * 100)));
};

const ModernTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div className="bizdb-tooltip">
      {label && <div className="bizdb-tooltip-label">{label}</div>}
      {payload.map((item, index) => (
        <div key={index} className="bizdb-tooltip-row">
          <span
            className="bizdb-tooltip-dot"
            style={{ background: item.color || item.fill }}
          />
          <span>{item.name || item.dataKey}</span>
          <strong>{item.value}</strong>
        </div>
      ))}
    </div>
  );
};

const EmptyState = ({ label = "No data available" }) => (
  <div className="bizdb-empty">
    <FiArchive size={24} />
    <span>{label}</span>
  </div>
);

const KpiCard = ({ icon: Icon, title, value, helper, tone = "blue" }) => (
  <div className={`bizdb-kpi bizdb-tone-${tone}`}>
    <div className="bizdb-kpi-icon">
      <Icon size={20} />
    </div>
    <div className="bizdb-kpi-content">
      <div className="bizdb-kpi-title">{title}</div>
      <div className="bizdb-kpi-value">{value}</div>
      {helper && <div className="bizdb-kpi-helper">{helper}</div>}
    </div>
  </div>
);

const SummaryCard = ({ icon: Icon, title, value, note, progress, tone = "blue" }) => (
  <div className={`bizdb-summary-card bizdb-tone-${tone}`}>
    <div className="bizdb-summary-top">
      <div className="bizdb-summary-icon">
        <Icon size={18} />
      </div>
      <div>
        <h3>{title}</h3>
        <p>{note}</p>
      </div>
    </div>
    <div className="bizdb-summary-value">{value}</div>
    {typeof progress === "number" && (
      <div className="bizdb-progress">
        <div className="bizdb-progress-fill" style={{ width: `${progress}%` }} />
      </div>
    )}
  </div>
);

const SectionHeader = ({ title, subtitle, icon: Icon }) => (
  <div className="bizdb-section-header">
    <div className="bizdb-section-icon">
      <Icon size={18} />
    </div>
    <div>
      <h2>{title}</h2>
      {subtitle && <p>{subtitle}</p>}
    </div>
  </div>
);

const ChartPanel = ({ title, subtitle, icon: Icon, children }) => (
  <div className="bizdb-panel">
    <SectionHeader title={title} subtitle={subtitle} icon={Icon} />
    <div className="bizdb-chart-body">{children}</div>
  </div>
);

const StatusPill = ({ children, tone = "blue" }) => (
  <span className={`bizdb-pill bizdb-pill-${tone}`}>{children}</span>
);

const DataTable = ({ columns, rows, emptyLabel }) => (
  <div className="bizdb-table-wrap">
    {rows.length === 0 ? (
      <EmptyState label={emptyLabel} />
    ) : (
      <table className="bizdb-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {columns.map((column) => (
                <td key={column.key}>
                  {column.render ? column.render(row, rowIndex) : row[column.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

export default function Dashboard() {
  const { Get } = useApi();
  const { formatCurrency } = useFormattingUtils();

  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;

    const load = async () => {
      try {
        const response = await Get("productrfqDashboard");
        setInv(response);
      } catch (error) {
        console.error("Dashboard error:", error);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [Get]);

  const data = inv || {};
  const rfq = data?.rfq || {};
  const vendor = data?.vendor || {};
  const po = data?.po || {};
  const finance = data?.finance || {};
  const charts = data?.charts || {};
  const topSell = data?.top_selling?.by_date_range || [];
  const thisWeek = data?.top_selling?.this_week || [];
  const topCats = data?.top_categories || [];
  const stock = data?.most_added_in_stock?.by_stock_arrived || [];
  const sales = data?.sales_summary || {};

  const monthlyPipeline = useMemo(
    () =>
      MONTHS.map((month, index) => {
        const rfqFound = (charts.rfq_monthly || []).find(
          (item) => new Date(item.month).getMonth() === index
        );
        const poFound = (charts.po_monthly || []).find(
          (item) => new Date(item.month).getMonth() === index
        );

        return {
          month,
          RFQs: rfqFound ? safeNumber(rfqFound.count) : 0,
          POs: poFound ? safeNumber(poFound.count) : 0,
        };
      }),
    [charts.rfq_monthly, charts.po_monthly]
  );

  const soldMonthly = useMemo(
    () =>
      MONTHS.map((month, index) => {
        const found = (charts.monthly_products_sold || []).find((item) => {
          const [, monthNumber] = String(item.month || "").split("-");
          return parseInt(monthNumber, 10) - 1 === index;
        });

        return { month, Sold: found ? safeNumber(found.total_sold) : 0 };
      }),
    [charts.monthly_products_sold]
  );

  const topSellPie = useMemo(
    () =>
      topSell.slice(0, 6).map((product) => ({
        name: product.product_name,
        value: safeNumber(product.total_sold_qty),
      })),
    [topSell]
  );

  const topCategoriesForTable = useMemo(
    () => [...topCats].sort((a, b) => safeNumber(b.sold_percentage) - safeNumber(a.sold_percentage)).slice(0, 8),
    [topCats]
  );

  const vendorApprovalRate = getPercentage(vendor.approved, vendor.total);
  const poDeliveryRate = getPercentage(po.delivered, po.total);
  const rfqClosureRate = getPercentage(rfq.closed, rfq.total);
  const pendingPOValue = safeNumber(finance.pending_delivery_value);
  const deliveredValue = safeNumber(finance.delivered_value);
  const totalPOValue = safeNumber(finance.total_po_value);
  const pendingRFQs = Math.max(
    safeNumber(rfq.total) - safeNumber(rfq.closed) - safeNumber(rfq.cancelled),
    0
  );

  const currentDateLabel = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  if (loading) {
    return (
      <div className="responsive-box bizdb-page">
        <style>{dashboardStyles}</style>
        <div className="bizdb-loading">
          <div className="bizdb-loading-orb" />
          <div>
            <h3>Preparing Business Dashboard</h3>
            <p>Loading inventory, RFQ, purchase and sales insights.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="responsive-box bizdb-page">
      <style>{dashboardStyles}</style>

      <div className="bizdb-shell">
        <section className="bizdb-hero">
          <div>
            <div className="bizdb-kicker">
              <span className="bizdb-kicker-dot" />
              Business View
            </div>
            <h1>Inventory Dashboard</h1>
            <p>
              Simple executive view for sales, RFQs, purchase orders, vendors and stock movement.
            </p>
            <div className="bizdb-date">{currentDateLabel}</div>
          </div>

          <div className="bizdb-hero-kpis">
            <KpiCard
              icon={FiDollarSign}
              title="Total Sales"
              value={formatCurrency(sales.total_sales || 0)}
              helper="Overall sales value"
              tone="green"
            />
            <KpiCard
              icon={FiShoppingCart}
              title="Items Sold"
              value={formatCount(sales.total_items_sold || 0)}
              helper="Current month"
              tone="blue"
            />
            <KpiCard
              icon={FiAlertCircle}
              title="Pending PO Value"
              value={formatCurrency(pendingPOValue)}
              helper="Delivery exposure"
              tone={pendingPOValue > 0 ? "red" : "green"}
            />
          </div>
        </section>

        <section className="bizdb-summary-grid">
          <SummaryCard
            icon={FiTarget}
            title="RFQ Closure"
            value={`${rfqClosureRate}%`}
            note={`${formatCount(rfq.closed || 0)} closed out of ${formatCount(rfq.total || 0)} RFQs`}
            progress={rfqClosureRate}
            tone="blue"
          />
          <SummaryCard
            icon={FiUsers}
            title="Vendor Health"
            value={`${vendorApprovalRate}%`}
            note={`${formatCount(vendor.approved || 0)} approved vendors`}
            progress={vendorApprovalRate}
            tone="purple"
          />
          <SummaryCard
            icon={FiTruck}
            title="PO Delivery"
            value={`${poDeliveryRate}%`}
            note={`${formatCount(po.delivered || 0)} delivered out of ${formatCount(po.total || 0)} POs`}
            progress={poDeliveryRate}
            tone="green"
          />
          <SummaryCard
            icon={FiClock}
            title="Pending RFQs"
            value={formatCount(pendingRFQs)}
            note="Open RFQs needing follow-up"
            progress={getPercentage(pendingRFQs, rfq.total)}
            tone={pendingRFQs > 0 ? "amber" : "green"}
          />
        </section>

        <section className="bizdb-focus-grid">
          <div className="bizdb-panel">
            <SectionHeader
              title="Management Snapshot"
              subtitle="Key business counts without operational clutter"
              icon={FiActivity}
            />

            <div className="bizdb-snapshot-grid">
              <div className="bizdb-snapshot-row">
                <span>RFQs</span>
                <strong>{formatCount(rfq.total || 0)}</strong>
                <StatusPill tone="blue">{formatCount(rfq.sent || 0)} Sent</StatusPill>
              </div>
              <div className="bizdb-snapshot-row">
                <span>Vendors</span>
                <strong>{formatCount(vendor.total || 0)}</strong>
                <StatusPill tone="purple">{formatCount(vendor.approved || 0)} Approved</StatusPill>
              </div>
              <div className="bizdb-snapshot-row">
                <span>Purchase Orders</span>
                <strong>{formatCount(po.total || 0)}</strong>
                <StatusPill tone="green">{formatCount(po.delivered || 0)} Delivered</StatusPill>
              </div>
              <div className="bizdb-snapshot-row">
                <span>Cancelled RFQs / POs</span>
                <strong>{formatCount(safeNumber(rfq.cancelled) + safeNumber(po.cancelled))}</strong>
                <StatusPill tone="red">Review</StatusPill>
              </div>
            </div>
          </div>

          <div className="bizdb-panel">
            <SectionHeader
              title="Finance Snapshot"
              subtitle="Purchase order value and pending exposure"
              icon={FiDollarSign}
            />

            <div className="bizdb-finance-list">
              <div>
                <span>Total PO Value</span>
                <strong>{formatCurrency(totalPOValue)}</strong>
              </div>
              <div>
                <span>Delivered Value</span>
                <strong className="bizdb-green">{formatCurrency(deliveredValue)}</strong>
              </div>
              <div>
                <span>Pending Delivery Value</span>
                <strong className={pendingPOValue > 0 ? "bizdb-red" : "bizdb-green"}>
                  {formatCurrency(pendingPOValue)}
                </strong>
              </div>
            </div>
          </div>
        </section>

        <section className="bizdb-chart-grid">
          <ChartPanel
            title="RFQ vs Purchase Order Trend"
            subtitle="Combined monthly trend for faster business review"
            icon={FiBarChart2}
          >
            {monthlyPipeline.every((item) => item.RFQs === 0 && item.POs === 0) ? (
              <EmptyState />
            ) : (
              <ResponsiveContainer width="100%" height={270}>
                <BarChart data={monthlyPipeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ModernTooltip />} />
                  <Bar dataKey="RFQs" fill="#2563eb" radius={[8, 8, 0, 0]} barSize={18} />
                  <Bar dataKey="POs" fill="#059669" radius={[8, 8, 0, 0]} barSize={18} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartPanel>

          <ChartPanel
            title="Monthly Products Sold"
            subtitle="Product movement trend across the year"
            icon={FiTrendingUp}
          >
            {soldMonthly.every((item) => item.Sold === 0) ? (
              <EmptyState />
            ) : (
              <ResponsiveContainer width="100%" height={270}>
                <BarChart data={soldMonthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ModernTooltip />} />
                  <Bar dataKey="Sold" fill="#d97706" radius={[8, 8, 0, 0]} barSize={22} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartPanel>
        </section>

        <section className="bizdb-bottom-grid">
          <div className="bizdb-panel">
            <SectionHeader
              title="Top Selling Products"
              subtitle="Best-performing products in the selected range"
              icon={FiPieChart}
            />
            {topSellPie.length === 0 ? (
              <EmptyState />
            ) : (
              <ResponsiveContainer width="100%" height={270}>
                <PieChart>
                  <Pie
                    data={topSellPie}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={86}
                    innerRadius={52}
                    paddingAngle={4}
                    label={({ name }) => String(name || "").split(" ")[0]}
                  >
                    {topSellPie.map((_, index) => (
                      <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} units`, "Sold"]} />
                  <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="bizdb-panel">
            <SectionHeader
              title="This Week: Products to Watch"
              subtitle="Fast-moving products for quick decision-making"
              icon={FiShoppingCart}
            />
            <DataTable
              rows={thisWeek.slice(0, 8)}
              emptyLabel="No weekly product sales available"
              columns={[
                {
                  key: "product_name",
                  label: "Product",
                  render: (row) => <strong className="bizdb-table-title">{row.product_name}</strong>,
                },
                {
                  key: "total_sold_qty",
                  label: "Qty",
                  render: (row) => <StatusPill tone="blue">{formatCount(row.total_sold_qty)}</StatusPill>,
                },
                {
                  key: "total_sales",
                  label: "Sales",
                  render: (row) => <span className="bizdb-money">{formatCurrency(row.total_sales || 0)}</span>,
                },
              ]}
            />
          </div>
        </section>

        <section className="bizdb-bottom-grid">
          <div className="bizdb-panel">
            <SectionHeader
              title="Top Categories"
              subtitle="Category health by sold percentage"
              icon={FiPieChart}
            />
            <DataTable
              rows={topCategoriesForTable}
              emptyLabel="No category data available"
              columns={[
                {
                  key: "category",
                  label: "Category",
                  render: (row) => <strong className="bizdb-table-title">{row.category}</strong>,
                },
                {
                  key: "sold_percentage",
                  label: "Sold %",
                  render: (row) => <StatusPill tone="amber">{safeNumber(row.sold_percentage)}%</StatusPill>,
                },
                {
                  key: "progress",
                  label: "Progress",
                  render: (row, index) => (
                    <div className="bizdb-progress bizdb-progress-table">
                      <div
                        className="bizdb-progress-fill"
                        style={{
                          width: `${Math.min(safeNumber(row.sold_percentage), 100)}%`,
                          background: PIE_COLORS[index % PIE_COLORS.length],
                        }}
                      />
                    </div>
                  ),
                },
              ]}
            />
          </div>

          <div className="bizdb-panel">
            <SectionHeader
              title="Recent Stock Arrivals"
              subtitle="Products most added to stock"
              icon={FiArchive}
            />
            <DataTable
              rows={stock.slice(0, 8)}
              emptyLabel="No stock arrival data available"
              columns={[
                {
                  key: "product_name",
                  label: "Product",
                  render: (row) => <strong className="bizdb-table-title">{row.product_name}</strong>,
                },
                {
                  key: "total_stock_arrived",
                  label: "Qty",
                  render: (row) => <StatusPill tone="purple">{formatCount(row.total_stock_arrived)}</StatusPill>,
                },
                {
                  key: "vendor",
                  label: "Vendor",
                  render: (row) => <span className="bizdb-muted">{row.vendor || "—"}</span>,
                },
              ]}
            />
          </div>
        </section>
      </div>
    </div>
  );
}

const dashboardStyles = `
  .bizdb-page {
    min-height: 100vh;
    padding: 10px 12px 28px;
    background:
      radial-gradient(circle at top left, rgba(37, 99, 235, 0.08), transparent 28%),
      radial-gradient(circle at top right, rgba(16, 185, 129, 0.07), transparent 28%),
      linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
    color: #0f172a;
  }

  .bizdb-shell {
    max-width: 1500px;
    margin: 0 auto;
  }

  .bizdb-hero {
    position: relative;
    overflow: hidden;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(420px, 0.8fr);
    gap: 18px;
    align-items: center;
    padding: 22px;
    margin-bottom: 14px;
    border-radius: 28px;
    color: #ffffff;
    background: linear-gradient(135deg, #020617 0%, #111827 56%, #075985 100%);
    box-shadow: 0 24px 70px rgba(15, 23, 42, 0.22);
  }

  .bizdb-hero::after {
    content: "";
    position: absolute;
    width: 320px;
    height: 320px;
    right: -120px;
    top: -160px;
    border-radius: 999px;
    background: rgba(56, 189, 248, 0.16);
  }

  .bizdb-hero > * {
    position: relative;
    z-index: 1;
  }

  .bizdb-kicker {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 11px;
    border-radius: 999px;
    margin-bottom: 10px;
    font-size: 11px;
    font-weight: 950;
    color: #e0f2fe;
    background: rgba(255, 255, 255, 0.13);
    border: 1px solid rgba(255, 255, 255, 0.22);
  }

  .bizdb-kicker-dot {
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: #22c55e;
    box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.16);
  }

  .bizdb-hero h1 {
    margin: 0;
    font-size: 31px;
    line-height: 1.08;
    font-weight: 950;
    letter-spacing: -0.045em;
  }

  .bizdb-hero p {
    max-width: 720px;
    margin: 8px 0 0;
    color: rgba(255, 255, 255, 0.80);
    font-size: 13px;
    font-weight: 700;
    line-height: 1.6;
  }

  .bizdb-date {
    display: inline-flex;
    margin-top: 13px;
    padding: 7px 11px;
    border-radius: 999px;
    color: #bfdbfe;
    background: rgba(255, 255, 255, 0.10);
    border: 1px solid rgba(255, 255, 255, 0.18);
    font-size: 11px;
    font-weight: 900;
  }

  .bizdb-hero-kpis {
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .bizdb-kpi,
  .bizdb-summary-card,
  .bizdb-panel {
    background: rgba(255, 255, 255, 0.96);
    border: 1px solid #dbe3ec;
    box-shadow: 0 16px 42px rgba(15, 23, 42, 0.075);
  }

  .bizdb-kpi {
    display: flex;
    align-items: center;
    gap: 13px;
    min-height: 82px;
    padding: 13px;
    border-radius: 20px;
  }

  .bizdb-kpi-icon,
  .bizdb-summary-icon,
  .bizdb-section-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--bizdb-accent);
    background: var(--bizdb-soft);
    border: 1px solid var(--bizdb-border);
    flex-shrink: 0;
  }

  .bizdb-kpi-icon {
    width: 44px;
    height: 44px;
    border-radius: 15px;
  }

  .bizdb-kpi-title {
    color: #475569;
    font-size: 11px;
    font-weight: 950;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .bizdb-kpi-value {
    margin-top: 2px;
    color: #0f172a;
    font-size: 21px;
    font-weight: 950;
    letter-spacing: -0.035em;
  }

  .bizdb-kpi-helper {
    color: #64748b;
    font-size: 11px;
    font-weight: 750;
  }

  .bizdb-summary-grid,
  .bizdb-focus-grid,
  .bizdb-chart-grid,
  .bizdb-bottom-grid {
    display: grid;
    gap: 14px;
    margin-bottom: 14px;
  }

  .bizdb-summary-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .bizdb-focus-grid,
  .bizdb-chart-grid,
  .bizdb-bottom-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .bizdb-summary-card {
    border-radius: 22px;
    padding: 15px;
  }

  .bizdb-summary-top {
    display: flex;
    gap: 11px;
    align-items: flex-start;
  }

  .bizdb-summary-icon {
    width: 42px;
    height: 42px;
    border-radius: 15px;
  }

  .bizdb-summary-card h3 {
    margin: 0;
    color: #0f172a;
    font-size: 14px;
    font-weight: 950;
  }

  .bizdb-summary-card p {
    margin: 4px 0 0;
    color: #64748b;
    font-size: 11px;
    font-weight: 750;
    line-height: 1.45;
  }

  .bizdb-summary-value {
    margin-top: 14px;
    color: #0f172a;
    font-size: 26px;
    font-weight: 950;
    letter-spacing: -0.04em;
  }

  .bizdb-panel {
    border-radius: 24px;
    padding: 16px;
  }

  .bizdb-section-header {
    display: flex;
    align-items: center;
    gap: 11px;
    margin-bottom: 14px;
  }

  .bizdb-section-icon {
    width: 40px;
    height: 40px;
    border-radius: 15px;
    --bizdb-accent: #0f172a;
    --bizdb-soft: #f8fafc;
    --bizdb-border: #dbe3ec;
  }

  .bizdb-section-header h2 {
    margin: 0;
    color: #0f172a;
    font-size: 17px;
    font-weight: 950;
    letter-spacing: -0.025em;
  }

  .bizdb-section-header p {
    margin: 3px 0 0;
    color: #64748b;
    font-size: 12px;
    font-weight: 750;
  }

  .bizdb-snapshot-grid,
  .bizdb-finance-list {
    display: grid;
    gap: 10px;
  }

  .bizdb-snapshot-row,
  .bizdb-finance-list > div {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto;
    align-items: center;
    gap: 10px;
    padding: 11px 12px;
    border-radius: 16px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
  }

  .bizdb-finance-list > div {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .bizdb-snapshot-row span,
  .bizdb-finance-list span {
    color: #475569;
    font-size: 12px;
    font-weight: 850;
  }

  .bizdb-snapshot-row strong,
  .bizdb-finance-list strong {
    color: #0f172a;
    font-size: 15px;
    font-weight: 950;
  }

  .bizdb-green {
    color: #059669 !important;
  }

  .bizdb-red {
    color: #dc2626 !important;
  }

  .bizdb-progress {
    width: 100%;
    height: 8px;
    margin-top: 10px;
    border-radius: 999px;
    background: #e2e8f0;
    overflow: hidden;
  }

  .bizdb-progress-table {
    min-width: 130px;
    margin-top: 0;
  }

  .bizdb-progress-fill {
    height: 100%;
    border-radius: inherit;
    background: var(--bizdb-accent);
  }

  .bizdb-chart-body {
    min-height: 270px;
  }

  .bizdb-empty {
    min-height: 190px;
    border: 1px dashed #cbd5e1;
    border-radius: 18px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: #64748b;
    font-size: 13px;
    font-weight: 850;
    background: #f8fafc;
  }

  .bizdb-table-wrap {
    width: 100%;
    overflow-x: auto;
  }

  .bizdb-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0 7px;
    min-width: 560px;
  }

  .bizdb-table th {
    padding: 10px 12px;
    color: #64748b;
    background: #f8fafc;
    border-top: 1px solid #e2e8f0;
    border-bottom: 1px solid #e2e8f0;
    text-align: left;
    font-size: 11px;
    font-weight: 950;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
  }

  .bizdb-table th:first-child {
    border-left: 1px solid #e2e8f0;
    border-radius: 13px 0 0 13px;
  }

  .bizdb-table th:last-child {
    border-right: 1px solid #e2e8f0;
    border-radius: 0 13px 13px 0;
  }

  .bizdb-table td {
    padding: 11px 12px;
    background: #ffffff;
    border-top: 1px solid #e2e8f0;
    border-bottom: 1px solid #e2e8f0;
    color: #334155;
    font-size: 13px;
    font-weight: 750;
    vertical-align: middle;
  }

  .bizdb-table td:first-child {
    border-left: 1px solid #e2e8f0;
    border-radius: 13px 0 0 13px;
  }

  .bizdb-table td:last-child {
    border-right: 1px solid #e2e8f0;
    border-radius: 0 13px 13px 0;
  }

  .bizdb-table-title {
    color: #0f172a;
    font-weight: 950;
  }

  .bizdb-muted {
    color: #64748b;
  }

  .bizdb-money {
    color: #059669;
    font-weight: 950;
  }

  .bizdb-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 34px;
    padding: 5px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 950;
    white-space: nowrap;
  }

  .bizdb-pill-blue {
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
  }

  .bizdb-pill-green {
    background: #ecfdf5;
    color: #047857;
    border: 1px solid #a7f3d0;
  }

  .bizdb-pill-purple {
    background: #f3e8ff;
    color: #7c3aed;
    border: 1px solid #ddd6fe;
  }

  .bizdb-pill-amber {
    background: #fffbeb;
    color: #b45309;
    border: 1px solid #fde68a;
  }

  .bizdb-pill-red {
    background: #fef2f2;
    color: #b91c1c;
    border: 1px solid #fecaca;
  }

  .bizdb-tooltip {
    background: #ffffff;
    border: 1px solid #dbe3ec;
    border-radius: 14px;
    box-shadow: 0 18px 42px rgba(15, 23, 42, 0.16);
    padding: 9px 10px;
    min-width: 130px;
  }

  .bizdb-tooltip-label {
    color: #0f172a;
    font-size: 12px;
    font-weight: 950;
    margin-bottom: 6px;
  }

  .bizdb-tooltip-row {
    display: flex;
    align-items: center;
    gap: 7px;
    color: #64748b;
    font-size: 12px;
    font-weight: 750;
  }

  .bizdb-tooltip-row strong {
    margin-left: auto;
    color: #0f172a;
  }

  .bizdb-tooltip-dot {
    width: 8px;
    height: 8px;
    border-radius: 999px;
  }

  .bizdb-tone-blue {
    --bizdb-accent: #2563eb;
    --bizdb-soft: #eff6ff;
    --bizdb-border: #bfdbfe;
  }

  .bizdb-tone-green {
    --bizdb-accent: #059669;
    --bizdb-soft: #ecfdf5;
    --bizdb-border: #a7f3d0;
  }

  .bizdb-tone-purple {
    --bizdb-accent: #7c3aed;
    --bizdb-soft: #f3e8ff;
    --bizdb-border: #ddd6fe;
  }

  .bizdb-tone-red {
    --bizdb-accent: #dc2626;
    --bizdb-soft: #fef2f2;
    --bizdb-border: #fecaca;
  }

  .bizdb-tone-amber {
    --bizdb-accent: #d97706;
    --bizdb-soft: #fffbeb;
    --bizdb-border: #fde68a;
  }

  .bizdb-loading {
    min-height: 420px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 18px;
    color: #0f172a;
  }

  .bizdb-loading h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 950;
  }

  .bizdb-loading p {
    margin: 5px 0 0;
    color: #64748b;
    font-size: 13px;
    font-weight: 750;
  }

  .bizdb-loading-orb {
    width: 54px;
    height: 54px;
    border-radius: 999px;
    border: 5px solid #dbeafe;
    border-top-color: #2563eb;
    animation: bizdb-spin 0.8s linear infinite;
  }

  @keyframes bizdb-spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 1250px) {
    .bizdb-hero {
      grid-template-columns: 1fr;
    }

    .bizdb-summary-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .bizdb-focus-grid,
    .bizdb-chart-grid,
    .bizdb-bottom-grid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 768px) {
    .bizdb-page {
      padding: 8px 8px 22px;
    }

    .bizdb-hero {
      border-radius: 22px;
      padding: 18px;
    }

    .bizdb-hero h1 {
      font-size: 25px;
    }

    .bizdb-summary-grid {
      grid-template-columns: 1fr;
    }

    .bizdb-panel,
    .bizdb-summary-card {
      border-radius: 20px;
      padding: 14px;
    }

    .bizdb-snapshot-row {
      grid-template-columns: 1fr;
      align-items: flex-start;
    }
  }
`;
